/**
 * Rótulos e formatos dos e-mails de acompanhamento no Admin Geral
 * (docs/features/onboarding/README.md). Usado pela tela Acompanhamento e pelo card da empresa.
 */

export const TEMPLATE_ENABLED = {
  on: { label: 'Ligado', color: '#1B5E20', bg: '#E8F5E9' },
  off: { label: 'Desligado', color: '#64748B', bg: '#F1F5F9' },
};

export const SEND_STATUS = {
  sent: { label: 'Enviado', color: '#1B5E20', bg: '#E8F5E9' },
  pending: { label: 'Enviando', color: '#0D47A1', bg: '#E3F2FD' },
  failed: { label: 'Falhou', color: '#C62828', bg: '#FDECEA' },
  skipped: { label: 'Pulado', color: '#64748B', bg: '#F1F5F9' },
};

/** Status de cada evento da simulação */
export const SIMULATION_STATUS = {
  sent: { label: 'Enviado', color: '#1B5E20', bg: '#E8F5E9' },
  skipped: { label: 'Pulado', color: '#64748B', bg: '#F1F5F9' },
  waiting: { label: 'Aguardando', color: '#0D47A1', bg: '#E3F2FD' },
  legacy: { label: 'Rotina antiga', color: '#8D6E00', bg: '#FFF8E1' },
};

/** Períodos da simulação, em dias */
export const SIMULATION_DAYS = [7, 14, 30];

const DISCOVERY_STAGES = ['trial_discovery_one'];

/** "dias 2 a 3", "7 dias antes do término" ou "5 dias depois do término" */
const stageHint = (stage) => {
  if (stage.notice) return `${stage.notice} ${stage.notice === 1 ? 'dia' : 'dias'} antes do término`;
  if (stage.after) return `${stage.after} ${stage.after === 1 ? 'dia' : 'dias'} depois do término, só para quem não assinou`;
  if (Array.isArray(stage.window)) return `dias ${stage.window[0]} a ${stage.window[1]} do teste`;
  return '';
};

/**
 * Modelos agrupados por etapa da jornada, na ordem das etapas (a API já manda ordenado).
 * As descobertas (R01 a R14) servem o D14 e ganham um grupo próprio logo depois dele;
 * as revisões anteriores (só histórico) ficam no fim.
 * @returns {Array<{ key: string, code: string, label: string, hint: string, emptyText: string, templates: Array }>}
 */
export const groupTemplatesByStage = (templates, stages) => {
  const of = (stageKey) => templates.filter((item) => item.variant?.stage === stageKey);
  const groups = [];
  (stages || []).forEach((stage) => {
    groups.push({
      key: stage.key,
      code: stage.code,
      label: stage.label,
      hint: stageHint(stage),
      emptyText: DISCOVERY_STAGES.includes(stage.key) ? 'Esta etapa usa as descobertas (R01 a R14), logo abaixo.' : 'Sem modelos nesta etapa.',
      templates: of(stage.key),
    });
    if (stage.key === 'trial_discovery_one') {
      groups.push({ key: 'discovery', code: 'D14', label: 'Descobertas (D14)', hint: 'um recurso por etapa, conforme o uso da conta', emptyText: 'Sem descobertas.', templates: of('discovery') });
    }
  });
  const known = new Set([...(stages || []).map((stage) => stage.key), 'discovery', 'retired']);
  const others = templates.filter((item) => !known.has(item.variant?.stage));
  if (others.length > 0) groups.push({ key: 'other', code: '', label: 'Outros modelos', hint: '', emptyText: '', templates: others });
  const retired = of('retired');
  if (retired.length > 0) groups.push({ key: 'retired', code: '', label: 'Revisão anterior', hint: 'só histórico, não sai mais', emptyText: '', templates: retired });
  return groups;
};

/** "E02A · Configuração não iniciada" */
export const variantTitle = (template) => [template.variant?.code, template.variant?.label].filter(Boolean).join(' · ') || template.key;

/** "12 (40%)" sobre os enviados */
export const withPercent = (value, base) => (base > 0 ? `${value} (${Math.round((value / base) * 100)}%)` : String(value));

/** Rótulo de uma chave numa lista [{ key, label }] */
export const labelOf = (list, key) => (Array.isArray(list) ? list.find((item) => item.key === key)?.label : null) || key || '—';

/** Campos do editor a partir de um modelo da API */
export const formFromTemplate = (template) => ({
  key: template.key,
  subject: template.subject || '',
  preheader: template.preheader || '',
  body: template.body || '',
  button_label: template.button_label || '',
  button_target: template.button_target || '',
  plain: Boolean(template.plain),
  enabled: Boolean(template.enabled),
});
